/**
 * Toda a copy da LP /magistral (farmácias de manipulação) vive aqui.
 * Permite teste A/B de headline sem tocar nos componentes.
 */

/** Número que recebe as conversas (link). O rótulo exibido segue com DDD 11. */
export const WHATSAPP_NUMBER = "553832208706";

/** Origens de clique rastreadas na LP (uma por botão). */
export type MagistralOrigin =
  | "header"
  | "hero"
  | "recompra"
  | "integracao_fc"
  | "cta_final"
  | "float"
  | "sticky_mobile"
  | "footer";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "gclid",
  "fbclid",
] as const;

/**
 * Monta o link do WhatsApp comercial com a mensagem da LP + UTMs da sessão.
 * As UTMs são lidas da URL atual e, na falta, da sessão (via readAttributionParams).
 */
export function buildWhatsAppLink(origem: MagistralOrigin | string): string {
  const base = `Olá! Sou de uma farmácia de manipulação e quero o diagnóstico do meu atendimento. (origem: ${origem})`;

  let utmSuffix = "";
  if (typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      const pairs: string[] = [];
      for (const key of UTM_KEYS) {
        const value = params.get(key);
        if (value) pairs.push(`${key}=${value}`);
      }
      if (pairs.length) utmSuffix = ` [${pairs.join(" | ")}]`;
    } catch {
      /* noop */
    }
  }

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(base + utmSuffix)}`;
}

/* ---------------------------------- SEO ---------------------------------- */

export const MAGISTRAL_SEO = {
  url: "https://kentro.com.br/magistral",
  title: "Kentro para Farmácias de Manipulação | Chat, CRM e IA no WhatsApp oficial",
  description:
    "Centralize todos os WhatsApps da sua farmácia de manipulação, acompanhe cada orçamento de fórmula em funil e recupere a recompra de uso contínuo. Diagnóstico gratuito.",
  ogImage: "https://kentro.com.br/og/og-default.png",
};

/* --------------------------------- Header -------------------------------- */

export const MAGISTRAL_NAV = [
  { label: "Como funciona", href: "#metodo" },
  { label: "Fórmula Certa", href: "#formula-certa" },
  { label: "Resultados", href: "#resultados" },
  { label: "Dúvidas", href: "#duvidas" },
];

export const HEADER_CTA = "Falar com especialista";

/* ---------------------------------- Hero --------------------------------- */

export const HERO = {
  badge: "Para farmácias de manipulação",
  h1: "Sua farmácia não perde venda no balcão. Perde no WhatsApp que ninguém respondeu.",
  sub: "A Kentro centraliza todos os números da sua farmácia em um só lugar, organiza cada orçamento de fórmula em funil e avisa o paciente antes do uso contínuo acabar. Chat + CRM + IA, na API oficial do WhatsApp.",
  cta: "Quero um diagnóstico do meu atendimento",
  microcopy:
    "Conversa direta no WhatsApp com um especialista em farmácias. Sem formulário, sem compromisso.",
  scrollLink: "Ver como funciona ↓",
  image: {
    name: "hero-vitalis",
    alt: "Aplicativo da Kentro no celular mostrando o painel da farmácia: R$ 18,4 mil apresentados ao cliente, 61,4% de conversão, 68 orçamentos e 42 convertidos",
  },
  credibility: [
    "+1.600 empresas usando a Kentro",
    "+14.670 atendentes na plataforma todos os dias",
    "API oficial WhatsApp Business (Meta)",
    "LGPD dados criptografados",
  ],
};

/* ------------------------------- 2. Dor ---------------------------------- */

export const PAIN = {
  eyebrow: "Reconhece alguma dessas?",
  h2: "Se três dessas cenas acontecem na sua farmácia, o problema não é a equipe. É a estrutura.",
  cards: [
    {
      icon: "FileSearch",
      title: "A receita sumiu na conversa",
      text: "Chegou como foto às 9h, o atendente saiu às 18h, e ninguém acha mais o orçamento.",
    },
    {
      icon: "MessageSquareDashed",
      title: '"Vou ver e te retorno"',
      text: "O retorno não acontece. O paciente manipula na farmácia da esquina no dia seguinte.",
    },
    {
      icon: "Smartphone",
      title: "Cada balconista com um número",
      text: "Quando um atendente sai da farmácia, a carteira de clientes dele sai junto.",
    },
    {
      icon: "CalendarX",
      title: "Uso contínuo que não volta",
      text: "A fórmula do paciente acabou há 12 dias. Ninguém avisou. Ninguém recomprou.",
    },
    {
      icon: "ShieldAlert",
      title: "Risco de banimento",
      text: "Número não oficial, disparo manual, conta derrubada em pleno horário comercial.",
    },
    {
      icon: "BarChart3",
      title: "Você não tem o número",
      text: "Quantos orçamentos você mandou este mês? Quantos viraram fórmula? Ninguém sabe responder.",
    },
  ],
  closing:
    "Farmácia de manipulação não vive de venda por impulso. Vive de prescrição, prazo e recompra. Os três se perdem na mesma conversa desorganizada.",
};

/* ----------------------------- 3. Método --------------------------------- */

export const METHOD = {
  h2: "Método Kentro Farma: três movimentos para transformar conversa em fórmula manipulada.",
  steps: [
    {
      number: "01",
      title: "Centralizar",
      text: "Todos os números da farmácia, o Instagram e o webchat entram em uma caixa só, na API oficial da Meta. Sem trocar o número que seus pacientes já conhecem. Cada conversa tem dono, histórico e responsável.",
      icon: "Inbox",
    },
    {
      number: "02",
      title: "Acompanhar",
      text: "Cada orçamento vira um card em um funil visível: recebido → orçado → aprovado → em manipulação → pronto → entregue. Nada avança sozinho e nada some. Você abre o painel e vê onde cada fórmula parou.",
      icon: "KanbanSquare",
    },
    {
      number: "03",
      title: "Recomprar",
      text: "A Kentro calcula quando a fórmula de uso contínuo está acabando e dispara a lembrança no tempo certo, com o histórico daquele paciente na mão do atendente. A recompra deixa de depender da memória de alguém.",
      icon: "Repeat",
    },
  ],
  seal:
    "Implantação guiada · sua equipe treinada na Kentro Academy · integra com o ERP que você já usa",
};

/* ----------------------------- 4. Pilares -------------------------------- */

export const PILLARS = {
  h2: "Chat, CRM e IA. Feitos para quem atende paciente, não para quem vende software.",
  items: [
    {
      tag: "CHAT",
      title: "Um número, a farmácia inteira dentro",
      icon: "MessagesSquare",
      bullets: [
        "WhatsApp oficial (Meta), Instagram Direct, e-mail, voz e webchat na mesma tela",
        "Fila inteligente: distribui por loja, turno e especialidade do atendente",
        "Receita em foto ou PDF anexada ao cadastro do paciente, não solta na conversa",
        "Atendente saiu? A conversa e o cliente continuam na farmácia",
      ],
    },
    {
      tag: "CRM",
      title: "O funil da fórmula, do orçamento à retirada",
      icon: "ClipboardList",
      bullets: [
        "Etapas do pedido de manipulação visíveis em tempo real",
        "Histórico completo: fórmulas anteriores, prescritor, alergias, ticket, frequência",
        "Follow-up automático do orçamento que ficou sem resposta há 24h",
        "Carteira de prescritores: quais médicos indicam e quanto cada um traz",
      ],
    },
    {
      tag: "IA",
      title: "Atendimento 24/7 que sabe quando chamar um humano",
      icon: "Sparkles",
      bullets: [
        "Responde as perguntas repetidas: prazo, status do pedido, endereço, formas de pagamento",
        "Qualifica o pedido e já organiza a receita antes do time chegar",
        "Passa para o farmacêutico na hora em que o assunto vira orientação técnica ou clínica",
        "Nunca dá orientação farmacêutica no lugar do profissional",
      ],
    },
  ],
  regulatoryNote:
    "A IA da Kentro atua no atendimento operacional. Orientação farmacêutica, avaliação de prescrição e responsabilidade técnica seguem sendo do farmacêutico responsável.",
};

/* ------------- 5. Integração Kentro + Fórmula Certa (8 módulos) ----------- */

export type MagistralShot = { name: string; alt: string; maxW?: string };

export type MagistralModule = {
  id: string;
  eyebrow: string;
  h3: string;
  promise: string;
  body: string[];
  bulletsTitle?: string;
  bullets?: string[];
  blocks?: { title: string; text: string }[];
  cards?: { title: string; text: string; highlight?: boolean }[];
  highlight?: string;
  closing: string;
  shots: MagistralShot[];
  shotRatio?: string;
  fullWidthShots?: boolean;
};

export const FORMULA_CERTA = {
  eyebrow: "Kentro + Fórmula Certa",
  h2: "A Kentro lê o Fórmula Certa que sua farmácia já usa e devolve venda.",
  intro:
    "Os módulos abaixo estão em operação hoje em farmácias de manipulação. As telas são do produto real: trocamos apenas nomes de pacientes, nomes da equipe e a identificação da farmácia.",
  cta: "Conhecer a integração",
  modules: [
    {
      id: "modulo-aplicativo",
      eyebrow: "Módulo 01 · Aplicativo",
      h3: "Seu Fórmula Certa, agora também no celular.",
      promise: "Acompanhe os principais indicadores da sua farmácia de forma simples, rápida e segura.",
      body: [
        "Com o **Aplicativo Kentro integrado ao Fórmula Certa**, você acessa informações importantes do negócio direto do celular, com uma experiência moderna e feita para o dia a dia.",
      ],
      bulletsTitle: "Tenha na palma da mão:",
      bullets: [
        "Orçamentos e conversões",
        "Desempenho por filial",
        "Desempenho da equipe",
        "Prescritores",
        "Indicadores atualizados",
        "Acesso seguro por usuário",
        "Aplicativo personalizado com a identidade da sua empresa",
      ],
      closing:
        "Mais agilidade. Mais visão. Mais controle. Transforme os dados do **Fórmula Certa** em informações fáceis de acompanhar, onde você estiver.",
      shots: [
        { name: "app-atencao", alt: "Aplicativo Kentro: pontos de atenção da farmácia de manipulação" },
        { name: "app-desempenho", alt: "Aplicativo Kentro: desempenho por filial e por equipe" },
        { name: "app-colaboradores", alt: "Aplicativo Kentro: desempenho dos colaboradores da farmácia" },
      ],
      shotRatio: "aspect-[9/16]",
    },
    {
      id: "modulo-orcamentos",
      eyebrow: "Módulo 02 · Orçamentos",
      h3: "Orçamentos que vendem mais",
      promise: "Transforme o orçamento do Fórmula Certa em uma experiência profissional para o seu cliente.",
      body: [
        "A integração **Kentro + Fórmula Certa** consulta o orçamento diretamente no sistema e transforma os dados em uma apresentação personalizada, bonita e pronta para envio pelo WhatsApp.",
        "Com poucos cliques, sua equipe pode gerar um orçamento com **identidade visual da farmácia, nome do cliente, produtos, quantidades, valores, descontos e total final** — sem precisar montar tudo manualmente.",
      ],
      highlight: "Do Fórmula Certa direto para o WhatsApp.",
      bullets: [
        "Consulta automática do orçamento",
        "Identidade visual personalizada da farmácia",
        "Valores e descontos apresentados com clareza",
        "Geração de imagem pronta para envio",
        "Diferentes modelos de apresentação",
        "Mais agilidade para o atendente",
        "Uma experiência muito mais profissional para o cliente",
      ],
      closing:
        "Seu orçamento deixa de ser apenas um preço. **Ele passa a ser parte da experiência de compra.**",
      shots: [
        {
          name: "orc-peca",
          alt: "Peça de orçamento personalizada da farmácia gerada pela Kentro para envio no WhatsApp",
          maxW: "max-w-[420px]",
        },
        { name: "orc-consulta", alt: "Consulta do orçamento do Fórmula Certa dentro da Kentro" },
      ],
      shotRatio: "aspect-[4/5]",
    },
    {
      id: "modulo-campanhas",
      eyebrow: "Módulo 03 · Campanhas FC",
      h3: "Campanhas inteligentes com os dados do Fórmula Certa",
      promise: "Transforme informações do seu ERP em oportunidades automáticas de venda e relacionamento.",
      body: [
        "Com o **Campanhas FC**, a Kentro utiliza os dados do Fórmula Certa para identificar o momento certo de falar com cada cliente e executar campanhas de forma automatizada. Crie regras, personalize a comunicação e deixe a Kentro trabalhar continuamente na sua base.",
      ],
      cards: [
        {
          title: "PCP — Retirada",
          text: "Avise automaticamente o cliente conforme o pedido avança pelas etapas do PCP, reduzindo ligações e mensagens perguntando se a fórmula já está pronta.",
        },
        {
          title: "Compras Contínuas",
          text: "Identifique tratamentos e produtos de uso contínuo e lembre o cliente de realizar uma nova compra antes que o produto termine. **Mais recorrência. Mais recompra. Menos clientes esquecidos.**",
        },
        {
          title: "Aniversariante do Dia",
          text: "Identifique automaticamente quem está fazendo aniversário e envie uma comunicação personalizada na data certa. Uma oportunidade para **fortalecer o relacionamento, oferecer benefícios e trazer o cliente novamente para a farmácia.**",
        },
        {
          title: "Aniversariantes do Mês",
          text: "Crie campanhas especiais para todos os clientes que fazem aniversário no mês, permitindo ações promocionais e estratégias de relacionamento.",
        },
        {
          title: "Automação com regras personalizadas",
          text: "Você define **quando, para quem e como** cada campanha será executada. **Canal → Template → Regras → Simulação → Execução**. Antes de disparar, sua equipe pode configurar as condições da campanha e validar quem será impactado.",
          highlight: true,
        },
      ],
      closing:
        "Dados do Fórmula Certa trabalhando a favor das suas vendas. Automatize o relacionamento, aumente a recompra e transforme sua base de clientes em novas oportunidades todos os dias.",
      shots: [
        {
          name: "campanhas-fc",
          alt: "Painel de campanhas automatizadas com dados do Fórmula Certa na Kentro",
          maxW: "max-w-[1000px]",
        },
      ],
      shotRatio: "aspect-[16/9]",
      fullWidthShots: true,
    },
    {
      id: "modulo-pcp",
      eyebrow: "Módulo 04 · PCP",
      h3: "Acompanhe cada etapa da produção em tempo real",
      promise: "Seu atendimento sabe exatamente onde está o pedido do cliente.",
      body: [
        "Com o **PCP integrado ao Fórmula Certa**, sua equipe consegue consultar rapidamente o andamento dos manipulados e identificar **em qual etapa da produção cada pedido se encontra**. O funcionário pesquisa pelo cliente, orçamento ou telefone e visualiza as informações do pedido, status atual e histórico das etapas do PCP.",
      ],
      blocks: [
        {
          title: "Consulta rápida do pedido",
          text: "Localize facilmente pelo **nome do cliente, número do orçamento ou telefone**.",
        },
        {
          title: "Acompanhamento das etapas",
          text: "Veja se o pedido está **em produção, pronto para entrega, entregue** e acompanhe sua evolução pelas etapas registradas no PCP.",
        },
        {
          title: "Detalhes completos",
          text: "Consulte informações como **orçamento, prescritor, atendente responsável, entrada, previsão de retirada, forma farmacêutica, valor e contatos do cliente**.",
        },
        {
          title: "Atendimento muito mais rápido",
          text: 'Quando o cliente perguntar: **"Meu pedido já está pronto?"** o atendente consegue consultar na hora, sem precisar ligar ou mandar mensagem para o laboratório.',
        },
      ],
      closing:
        "Menos interrupções. Mais produtividade. O atendimento acompanha o PCP sem atrapalhar quem está produzindo.",
      shots: [
        { name: "pcp-lista", alt: "Lista de pedidos em produção no PCP integrado ao Fórmula Certa" },
        { name: "pcp-detalhe", alt: "Detalhe de um pedido manipulado com etapas do PCP na Kentro" },
      ],
      shotRatio: "aspect-[9/16]",
    },
    {
      id: "modulo-pagamentos",
      eyebrow: "Módulo 05 · Pagamentos",
      h3: "Pagamentos integrados, seguros e sem conferência manual",
      promise: "Cada funcionário gera seus próprios links. A Kentro confirma o pagamento automaticamente.",
      body: [
        "Com o **Kentro Pagamentos**, sua equipe pode gerar links de pagamento diretamente pelo sistema, com integração aos principais gateways e adquirentes do mercado. Cada funcionário possui sua própria visão e acompanha **somente os links e pagamentos que gerou**, enquanto gestores podem acompanhar o desempenho completo da equipe.",
      ],
      blocks: [
        {
          title: "Integração com grandes meios de pagamento",
          text: "Conecte sua operação a soluções como **Cielo, Rede, Mercado Pago, Asaas e outros gateways**, centralizando tudo dentro da Kentro.",
        },
        {
          title: "Link individual por funcionário",
          text: "Cada atendente gera o link para o seu cliente e acompanha seus próprios pagamentos, sem misturar informações com outros vendedores.",
        },
        {
          title: "Confirmação automática do pagamento",
          text: "Quando o pagamento é aprovado, a Kentro recebe a confirmação diretamente do gateway e atualiza o status automaticamente. **Sem precisar mandar comprovante. Sem funcionário conferindo extrato. Sem gerente olhando a conta bancária o tempo todo.**",
        },
        {
          title: "Mais segurança contra comprovantes falsos",
          text: "A confirmação não depende de uma imagem enviada pelo cliente. O status vem da própria integração com o meio de pagamento, reduzindo o risco de liberar um pedido baseado em um comprovante falso.",
        },
        {
          title: "Gestão completa da operação",
          text: "Acompanhe em um único painel: **Links gerados • Pagamentos confirmados • Valores recebidos • Pendentes • Conversão • Ticket médio • Desempenho por funcionário**",
        },
      ],
      closing:
        "Do atendimento à confirmação do pagamento, tudo conectado. Mais autonomia para sua equipe. Mais controle para a gestão. Mais segurança para sua empresa.",
      shots: [
        { name: "pag-painel", alt: "Painel do Kentro Pagamentos com links gerados e pagamentos confirmados" },
        { name: "pag-lista", alt: "Lista de links de pagamento por funcionário na Kentro" },
      ],
      shotRatio: "aspect-[4/3]",
    },
    {
      id: "modulo-dash",
      eyebrow: "Módulo 06 · DASH",
      h3: "Dashboard individual de vendas",
      promise: "Cada vendedor acompanha seus próprios resultados em tempo real.",
      body: [
        "Com o **DASH da Kentro integrado ao Fórmula Certa**, cada colaborador possui uma visão individual dos seus orçamentos e consegue acompanhar de perto sua performance comercial. Tudo é atualizado com os dados do Fórmula Certa, permitindo saber **quanto orçou, quanto converteu e onde ainda existem oportunidades de venda**.",
      ],
      blocks: [
        {
          title: "Controle dos próprios resultados",
          text: "**Orçamentos realizados • Valor orçado • Convertidos • Valor convertido • Taxa de conversão • Ticket médio • Clientes atendidos**",
        },
        {
          title: "Funil de conversão",
          text: "Visualize rapidamente quantos orçamentos foram realizados, quantos converteram em venda e quantos ainda estão sem conversão. Assim, o vendedor sabe **onde precisa agir para buscar mais resultados.**",
        },
        {
          title: "Clientes e prescritores",
          text: "Identifique os principais clientes e prescritores relacionados aos seus resultados, ajudando o vendedor a entender melhor sua própria carteira.",
        },
        {
          title: "Mais autonomia para o vendedor",
          text: "O funcionário não precisa esperar o fechamento do mês ou perguntar ao gerente como está sua performance. **Ele abre o painel e sabe na hora.**",
        },
      ],
      closing:
        "Cada vendedor passa a enxergar o próprio negócio. Mais controle. Mais responsabilidade. Mais oportunidades recuperadas. Mais vendas.",
      shots: [
        { name: "dash-quem-chamar", alt: "DASH da Kentro indicando quais clientes o vendedor deve chamar" },
        { name: "dash-resumo", alt: "Resumo individual de orçamentos e conversões no DASH da Kentro" },
        { name: "dash-clientes", alt: "Clientes e prescritores da carteira do vendedor no DASH da Kentro" },
      ],
      shotRatio: "aspect-[9/16]",
    },
    {
      id: "modulo-ranking",
      eyebrow: "Módulo 07 · Ranking",
      h3: "Ranking da Equipe",
      promise: "Transforme resultados em motivação para vender mais.",
      body: [
        "Com o **Ranking da Kentro integrado ao Fórmula Certa**, cada colaborador acompanha seu desempenho e compara seus resultados com o restante da equipe. A ideia é simples: **transformar metas e vendas em uma competição saudável**, estimulando a equipe a melhorar todos os dias.",
      ],
      blocks: [
        {
          title: "Ranking em tempo real",
          text: "Veja quem está liderando e acompanhe a classificação da equipe com períodos de **hoje, ontem, 7, 30, 90 dias ou personalizado**.",
        },
        {
          title: "Metas que estimulam a competição",
          text: "Cada funcionário consegue visualizar **quantos pontos faltam para subir de posição ou ultrapassar o próximo colocado**. Isso transforma o resultado em um objetivo claro e alcançável.",
        },
        {
          title: "Muito além de quem vendeu mais",
          text: "**Orçamentos • Conversões • Taxa de conversão • Ticket médio • Receita convertida • Score**",
        },
        {
          title: "Destaques automáticos",
          text: "**Líder da equipe • Maior evolução • Maior receita • Melhor conversão**",
        },
        {
          title: "Acompanhamento da equipe",
          text: "Gestores conseguem identificar rapidamente **quem está performando bem, quem está evoluindo e quem precisa de acompanhamento**.",
        },
      ],
      closing:
        "Mais competição. Mais engajamento. Mais resultado. Quando cada funcionário sabe onde está e o que precisa fazer para subir no ranking, a meta deixa de ser apenas um número e vira um desafio diário.",
      shots: [
        { name: "rank-painel", alt: "Painel do ranking da equipe da farmácia na Kentro" },
        { name: "rank-classificacao", alt: "Classificação dos atendentes no ranking da Kentro" },
      ],
      shotRatio: "aspect-[4/3]",
    },
    {
      id: "modulo-base-clientes",
      eyebrow: "Módulo 08 · Base de clientes",
      h3: "Conheça todo o histórico do seu cliente",
      promise: "Transforme a base do Fórmula Certa em inteligência para vender melhor.",
      body: [
        "Com a **Base de Clientes da Kentro**, sua equipe consegue consultar o histórico do cliente dentro da farmácia e entender rapidamente **o que ele já comprou, quanto já movimentou e o que deixou de comprar**. Em vez de enxergar apenas o atendimento atual, o vendedor passa a ter uma **visão completa do relacionamento do cliente com a farmácia**.",
      ],
      blocks: [
        {
          title: "Visão 360º do cliente",
          text: "**Dados cadastrais • Histórico de orçamentos • Compras anteriores • Valores • Fórmulas • Ativos • Quantidades • Descontos • Datas • Status**",
        },
        {
          title: "Saiba exatamente o que ele já comprou",
          text: "Abra qualquer orçamento anterior e visualize a composição das fórmulas, ativos, quantidades e valores. Assim, o atendente entende o histórico antes mesmo de iniciar uma nova negociação.",
        },
        {
          title: "Identifique clientes que deixaram de comprar",
          text: "O histórico ajuda a encontrar clientes que **compravam com frequência e pararam**, criando oportunidades para ações de recuperação e recompra.",
        },
        {
          title: "Venda com contexto, não no escuro",
          text: "Quando um cliente voltar a falar com a farmácia, sua equipe já consegue entender seu relacionamento anterior e conduzir um atendimento muito mais personalizado.",
        },
      ],
      closing:
        "Sua base de clientes já possui uma enorme quantidade de informação. A Kentro transforma esse histórico em inteligência para recuperar clientes, estimular recompras e gerar novas oportunidades de venda.",
      shots: [
        { name: "base-cliente", alt: "Ficha do cliente da farmácia com dados cadastrais na Kentro" },
        { name: "base-historico", alt: "Histórico de orçamentos e compras do cliente na Kentro" },
        { name: "base-formula", alt: "Composição da fórmula manipulada de um orçamento anterior na Kentro" },
      ],
      shotRatio: "aspect-[9/16]",
    },
  ] satisfies MagistralModule[],
};

/* ------------------------------ 6. Teste a IA ---------------------------- */

export const TEST_AI = {
  eyebrow: "Teste agora · IA Magistral",
  h2: "Em vez de imaginar, converse com a IA no WhatsApp.",
  body: "Faça as perguntas que seus pacientes fazem, teste as objeções que você ouve no balcão e veja onde ela passa a conversa para o farmacêutico.",
  // TODO: confirmar se haverá rota própria /magistral no formulário de IA
  primary: {
    label: "Experimentar agora",
    href: "https://formulario.kentro.com.br/farmacia?utm_source=site_kentro&utm_medium=lp_magistral&utm_campaign=experimente_ia&utm_content=magistral",
  },
  secondary: { label: "Ver todas as IAs", href: "/ia" },
};

/* --------------------------- 7. Recompra contínua ------------------------- */

export const RECOMPRA = {
  h2: "Seu maior faturamento não está no orçamento novo. Está no frasco que acabou ontem.",
  body: "Uma fórmula de 60 doses acaba em uma data previsível. A Kentro guarda essa data, avisa o paciente com antecedência e coloca a conversa na frente do atendente com um clique para reenviar a mesma fórmula. É a diferença entre esperar o paciente lembrar e fazer a farmácia lembrar por ele.",
  timeline: [
    "Dia 0 — fórmula entregue",
    "Dia 45 — Kentro alerta o time",
    "Dia 52 — mensagem no WhatsApp do paciente",
    "Dia 55 — nova manipulação aprovada",
  ],
  exampleBadge: "Exemplo hipotético",
  example:
    "Faça a conta com o seu número: se a sua farmácia entrega 300 fórmulas de uso contínuo por mês e recupera 15% das recompras que hoje se perdem, são 45 manipulações a mais. Multiplique pelo seu ticket médio.",
  note: "Cenário ilustrativo para você fazer a conta com os seus próprios números. Não é resultado prometido nem média de clientes.",
  cta: "Calcular isso com um especialista",
};

/* --------------------------------- 8. Prova ------------------------------- */

// TODO: substituir um dos cartões abaixo quando houver case real de farmácia de manipulação.
export const PROOF = {
  h2: "O que acontece quando o atendimento para de ser improviso.",
  kpis: [
    { value: "−45%", label: "no tempo médio de atendimento por canal", source: "Case Kentro — farmácias" },
    { value: "+32%", label: "na recompra de clientes em até 30 dias", source: "Case Kentro — farmácias" },
    { value: "−60%", label: "nas reclamações por demora de resposta", source: "Case Kentro — farmácias" },
    { value: "+28%", label: "em atendimentos fora do horário comercial", source: "Case Kentro — farmácias" },
  ],
  note: "Resultados reportados em cases Kentro no segmento de farmácias. Resultados variam conforme operação, volume e maturidade do processo.",
  video: {
    badge: "Case em vídeo · Farmácia",
    youtubeId: "UmXf5_WRhHo",
    company: "Farmácia Bom Preço · Wesley Danilo",
    quote: "Aumentou o contato com clientes e expandiu a base.",
    note: "Depoimento de cliente Kentro no varejo farmacêutico.",
  },
};

/* ------------------------------ 9. Antes/depois -------------------------- */

export const BEFORE_AFTER = {
  h2: "Antes e depois, no dia a dia.",
  beforeLabel: "Hoje",
  afterLabel: "Com a Kentro",
  rows: [
    { before: "4 celulares, 4 conversas paralelas", after: "1 caixa de entrada, toda a equipe dentro" },
    { before: "Orçamento respondido em horas", after: "Primeira resposta automática em segundos" },
    { before: "Receita perdida no meio do chat", after: "Receita anexada ao cadastro do paciente" },
    { before: "Recompra por memória do balconista", after: "Recompra por gatilho de data no sistema" },
    { before: "Número pessoal, risco de banimento", after: "API oficial da Meta, conta protegida" },
    { before: '"Acho que vendeu bem esse mês"', after: "Relatório por atendente, por loja e por prescritor" },
  ],
};

/* ------------------------- 10. Migração e segurança ---------------------- */

export const MIGRATION = {
  h2: "Trocar de sistema não pode parar a farmácia. Na Kentro, não para.",
  items: [
    {
      title: "Você não troca de número.",
      text: "A migração mantém o WhatsApp que seus pacientes já têm salvo.",
      icon: "Smartphone",
    },
    {
      title: "Implantação guiada, sem parar a operação.",
      text: "Onboarding conduzido pelo nosso time, com sua equipe treinada na Kentro Academy.",
      icon: "GraduationCap",
    },
    {
      title: "Integra com o que você já usa.",
      text: "APIs REST, webhooks e integração com ERPs de manipulação como Fórmula Certa e A Fórmula, entre outros.",
      icon: "Plug",
    },
    {
      title: "Dado de paciente é dado sensível.",
      text: "Criptografia, controle de acesso por perfil e operação aderente à LGPD.",
      icon: "Lock",
    },
  ],
};

/* ------------------------------ 11. CTA final ---------------------------- */

export const FINAL_CTA = {
  h2: "Em 15 minutos no WhatsApp, a gente te mostra onde sua farmácia está perdendo pedido.",
  body: "Você manda como funciona seu atendimento hoje: quantos números, quantos atendentes, quantos orçamentos por dia. A gente devolve um diagnóstico do que está vazando e quanto isso representa por mês. Se fizer sentido seguir, seguimos. Se não fizer, você fica com o diagnóstico do mesmo jeito.",
  cta: "Falar com um especialista em farmácias",
  microcopy: "Atendimento humano · seg a sex, 8h às 18h · resposta no mesmo dia útil",
  form: {
    nameLabel: "Nome",
    phoneLabel: "WhatsApp",
    agentsLabel: "Nº de atendentes",
    agentsOptions: ["1–3", "4–10", "11–20", "+20"],
    submit: "Prefiro que me chamem",
    success: "Recebemos seu contato. Um especialista em farmácias fala com você no próximo dia útil.",
    error: "Não conseguimos enviar agora. Tente novamente ou fale direto no WhatsApp.",
  },
};

/* ----------------------------------- FAQ --------------------------------- */

export const FAQ_TITLE = "Perguntas que todo dono de farmácia faz antes de trocar";

/** FAQ (7 perguntas) — alimenta o accordion e o JSON-LD FAQPage. */
export const FAQ: { q: string; a: string }[] = [
  {
    q: "Preciso trocar o número da farmácia?",
    a: "Não. A migração para a API oficial mantém o mesmo número que seus pacientes já usam.",
  },
  {
    q: "Minha equipe é pequena. Isso serve para mim?",
    a: "Sim. A Kentro roda em farmácias de uma loja e três atendentes até redes com dezenas. O que muda é o plano, não a ferramenta.",
  },
  {
    q: "Como funciona a implantação?",
    a: "O onboarding é conduzido pelo nosso time, com treinamento da sua equipe na Kentro Academy incluído. O cronograma depende do número de lojas, atendentes e integrações — a gente define isso com você no diagnóstico.",
  },
  {
    q: "A IA vai orientar meu paciente sobre a fórmula?",
    a: "Não. A IA responde o operacional — prazo, status, endereço, pagamento — e transfere para o farmacêutico assim que o assunto vira orientação técnica.",
  },
  {
    q: "Funciona junto com meu sistema de manipulação?",
    a: "Sim, via API e webhooks, e com integrações para os ERPs de manipulação do mercado, como Fórmula Certa e A Fórmula.",
  },
  {
    q: "E a LGPD, com dado de paciente?",
    a: "Dados criptografados, acesso por perfil, trilha de auditoria e tratamento aderente à LGPD.",
  },
  {
    q: "Quanto custa?",
    a: "O plano é definido por número de atendentes e volume de conversas. No diagnóstico a gente te passa o valor exato para o seu cenário.",
  },
];


/* --------------------------------- Footer -------------------------------- */

export const FOOTER = {
  legalName: "Barbalho Solutions Ltda. · CNPJ 43.406.507/0001-52",
  address: "Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP, CEP 06454-000",
  email: "contato@kentro.com.br",
  phone0800: "0800 550 2020",
  whatsappLabel: "WhatsApp (11) 91582-7716",
  social: [
    { label: "LinkedIn", href: "https://www.linkedin.com/company/kentro-sistemas" },
    { label: "Instagram", href: "https://www.instagram.com/kentrosistemas" },
    { label: "YouTube", href: "https://www.youtube.com/@kentrosistemas" },
    { label: "TikTok", href: "https://www.tiktok.com/@kentrosistemas" },
  ],
  legal: [
    { label: "Privacidade", href: "/privacidade" },
    { label: "Política de cookies", href: "/politica-de-cookies" },
    { label: "Aviso de privacidade", href: "/aviso-de-privacidade" },
    { label: "Canal de denúncias", href: "/ouvidoria" },
  ],
};

export const STICKY_CTA = "Quero meu diagnóstico gratuito";
